import { useCallback, useRef, useState } from 'react';

interface PdfDropZoneProps {
  onFile: (buffer: ArrayBuffer, name: string) => void;
  disabled?: boolean;
}

export function PdfDropZone({ onFile, disabled }: PdfDropZoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        setError("That doesn't look like a PDF.");
        return;
      }
      const buf = await file.arrayBuffer();
      onFile(buf, file.name);
    },
    [onFile],
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        if (disabled) return;
        const file = e.dataTransfer.files?.[0];
        if (file) void handleFile(file);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      className={[
        'group rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200',
        dragActive
          ? 'border-brand-400 bg-brand-50 ring-4 ring-brand-100'
          : 'border-slate-300 bg-white/70',
        disabled ? 'opacity-50' : 'cursor-pointer hover:border-brand-300 hover:bg-brand-50/40',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={`mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
          dragActive
            ? 'bg-brand-100 text-brand-700'
            : 'bg-slate-100 text-slate-400 group-hover:bg-brand-100 group-hover:text-brand-600'
        }`}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
          <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
      </span>
      <p className="text-sm font-semibold tracking-tight text-slate-800">Drop a PDF here</p>
      <p className="mt-1 text-xs text-slate-500">or click to choose a file</p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          // Reset so the same file can be picked twice in a row.
          e.target.value = '';
        }}
      />
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
